import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonAlert,
  IonBadge,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import {
  closeOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  cloudDownloadOutline,
  cloudUploadOutline,
  chevronDownOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import { SyncComparisonResult, ApprovalChoice, ChecklistItemDiff, FieldDiff } from '@/types/model/sync';

interface SyncComparisonModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  comparisonData: SyncComparisonResult | null;
  onCommit: (inspectionResultId: number, choice: ApprovalChoice) => Promise<void>;
}

const SyncComparisonModal: React.FC<SyncComparisonModalProps> = ({
  isOpen,
  onDidDismiss,
  comparisonData,
  onCommit,
}) => {
  const [approvalChoice, setApprovalChoice] = useState<ApprovalChoice | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const handleCommit = async (): Promise<void> => {
    if (!approvalChoice || !comparisonData) return;
    setIsCommitting(true);
    try {
      await onCommit(comparisonData.inspectionResultId, approvalChoice);
      setToastMessage('Sync committed successfully');
      setToastVariant('success');
      setShowToast(true);
      setApprovalChoice(null);
      onDidDismiss();
    } catch (error) {
      console.error('Commit failed:', error);
      setToastMessage('Failed to commit sync. Please try again.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsCommitting(false);
    }
  };

  const toggleChecklistItem = (id: number): void => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatTimestamp = (ts: string | null): string => {
    if (!ts) return 'Unknown';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const renderFieldRow = (diff: FieldDiff): React.ReactElement => (
    <div
      key={diff.field}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
        padding: '8px 12px',
        borderBottom: '1px solid var(--ion-color-light-shade)',
        background: diff.isDifferent ? 'var(--ion-color-danger-tint)' : 'transparent',
      }}
    >
      <IonText style={{ fontSize: '0.85rem', fontWeight: 500 }}>
        {diff.label}
      </IonText>
      <IonText
        style={{
          fontSize: '0.85rem',
          fontWeight: diff.isDifferent ? 600 : 400,
          color: diff.isDifferent && approvalChoice === 'local' ? 'var(--ion-color-success)' : undefined,
        }}
      >
        {formatValue(diff.localValue)}
      </IonText>
      <IonText
        style={{
          fontSize: '0.85rem',
          fontWeight: diff.isDifferent ? 600 : 400,
          color: diff.isDifferent && approvalChoice === 'server' ? 'var(--ion-color-success)' : undefined,
        }}
      >
        {formatValue(diff.serverValue)}
      </IonText>
    </div>
  );

  const renderChecklistDiff = (diff: ChecklistItemDiff): React.ReactElement => {
    const isExpanded = expandedItems.has(diff.checklistItemId);
    const hasDiff = diff.scoreDiff.isDifferent || diff.commentDiff.isDifferent ||
      diff.attachmentCountDiff.isDifferent || diff.attachmentNamesDiff.isDifferent;

    return (
      <div key={diff.checklistItemId} style={{ borderBottom: '1px solid var(--ion-color-light-shade)' }}>
        <div style={{ padding: '8px 12px 4px 12px', background: 'var(--ion-color-light)' }}>
          <IonText style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--ion-color-medium)' }}>
            Checklist Item  {diff.text}
          </IonText>
        </div>
        <div
          onClick={() => toggleChecklistItem(diff.checklistItemId)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 12px',
            cursor: 'pointer',
            background: hasDiff ? 'var(--ion-color-warning-tint)' : 'transparent',
          }}
        >
          <IonIcon icon={isExpanded ? chevronDownOutline : chevronForwardOutline} />
          {hasDiff && (
            <IonBadge color="warning" style={{ fontSize: '0.7rem' }}>Changed</IonBadge>
          )}
        </div>
        {isExpanded && (
          <div style={{ paddingLeft: '24px' }}>
            {renderFieldRow(diff.scoreDiff)}
            {renderFieldRow(diff.commentDiff)}
            {renderFieldRow(diff.attachmentCountDiff)}
            {diff.attachmentNamesDiff.isDifferent && renderFieldRow(diff.attachmentNamesDiff)}
          </div>
        )}
      </div>
    );
  };

  if (!comparisonData) return null;

  const isLocalOnly = comparisonData.status === 'local-only';

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss} style={{ '--width': '90vw', '--max-width': '900px', '--height': '90vh' }}>
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontSize: '1rem' }}>
              {comparisonData.lineItem} — Sync Comparison
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDidDismiss}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          <div style={{ padding: '16px' }}>
            {/* Metadata row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '16px',
              padding: '12px',
              borderRadius: '8px',
              background: 'var(--ion-color-light)',
            }}>
              <div>
                <IonText color="medium" style={{ fontSize: '0.75rem', display: 'block' }}>Group</IonText>
                <IonText style={{ fontWeight: 500 }}>{comparisonData.groupTitle}</IonText>
              </div>
              <div>
                <IonText color="medium" style={{ fontSize: '0.75rem', display: 'block' }}>Line Item</IonText>
                <IonText style={{ fontWeight: 500 }}>{comparisonData.lineItemTitle}</IonText>
              </div>
            </div>

            {/* Timestamps */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '16px',
              padding: '12px',
              borderRadius: '8px',
              background: 'var(--ion-color-light)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonIcon icon={cloudUploadOutline} color="primary" />
                <div>
                  <IonText color="medium" style={{ fontSize: '0.75rem', display: 'block' }}>Local Cached At</IonText>
                  <IonText style={{ fontSize: '0.85rem' }}>{formatTimestamp(comparisonData.localCachedAt)}</IonText>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonIcon icon={cloudDownloadOutline} color="secondary" />
                <div>
                  <IonText color="medium" style={{ fontSize: '0.75rem', display: 'block' }}>Server Updated</IonText>
                  <IonText style={{ fontSize: '0.85rem' }}>
                    {isLocalOnly ? 'Not on server' : formatTimestamp(comparisonData.serverUpdatedAt)}
                  </IonText>
                </div>
              </div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon
                icon={comparisonData.hasDifferences ? alertCircleOutline : checkmarkCircleOutline}
                color={comparisonData.hasDifferences ? 'warning' : 'success'}
                style={{ fontSize: '1.2rem' }}
              />
              <IonText style={{ fontWeight: 500 }}>
                {comparisonData.hasDifferences
                  ? `${comparisonData.fieldDiffs.filter(d => d.isDifferent).length} field(s) and ${comparisonData.checklistDiffs.length} checklist item(s) differ`
                  : 'Local and server data are in sync'}
              </IonText>
            </div>

            {/* Field comparison table */}
            {comparisonData.fieldDiffs.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <IonText style={{ fontWeight: 600, fontSize: '0.95rem', display: 'block', marginBottom: '8px' }}>
                  Inspection Fields
                </IonText>
                <div style={{ border: '1px solid var(--ion-color-light-shade)', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '8px',
                    padding: '8px 12px',
                    background: 'var(--ion-color-medium-tint)',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}>
                    <IonText>Field</IonText>
                    <IonText>Local</IonText>
                    <IonText>Server</IonText>
                  </div>
                  {comparisonData.fieldDiffs.map(renderFieldRow)}
                </div>
              </div>
            )}

            {/* Checklist comparison */}
            {comparisonData.checklistDiffs.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <IonText style={{ fontWeight: 600, fontSize: '0.95rem', display: 'block', marginBottom: '8px' }}>
                  Checklist Item Differences
                </IonText>
                <div style={{ border: '1px solid var(--ion-color-light-shade)', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '8px',
                    padding: '8px 12px',
                    background: 'var(--ion-color-medium-tint)',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}>
                    <IonText>Field</IonText>
                    <IonText>Local</IonText>
                    <IonText>Server</IonText>
                  </div>
                  {comparisonData.checklistDiffs.map(renderChecklistDiff)}
                </div>
              </div>
            )}

            {/* No differences message */}
            {!comparisonData.hasDifferences && (
              <div style={{
                textAlign: 'center',
                padding: '24px',
                background: 'var(--ion-color-success-tint)',
                borderRadius: '8px',
                marginBottom: '16px',
              }}>
                <IonIcon icon={checkmarkCircleOutline} color="success" style={{ fontSize: '2rem', marginBottom: '8px' }} />
                <IonText color="success" style={{ display: 'block', fontWeight: 500 }}>
                  All data is in sync — no action needed.
                </IonText>
              </div>
            )}

            {/* Approval section */}
            {comparisonData.hasDifferences && !isLocalOnly && (
              <div style={{
                padding: '16px',
                borderRadius: '8px',
                border: '2px solid var(--ion-color-primary-tint)',
                background: 'var(--ion-color-light)',
              }}>
                <IonText style={{ fontWeight: 600, fontSize: '0.95rem', display: 'block', marginBottom: '12px' }}>
                  Select version to approve
                </IonText>

                <IonSegment
                  value={approvalChoice || undefined}
                  onIonChange={e => setApprovalChoice(e.detail.value as ApprovalChoice)}
                  style={{ marginBottom: '12px' }}
                >
                  <IonSegmentButton value="local">
                    <IonLabel>
                      <IonIcon icon={cloudUploadOutline} style={{ marginRight: '4px' }} />
                      Local
                    </IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="server">
                    <IonLabel>
                      <IonIcon icon={cloudDownloadOutline} style={{ marginRight: '4px' }} />
                      Server
                    </IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                {approvalChoice && (
                  <IonText color="medium" style={{ fontSize: '0.8rem', display: 'block', marginBottom: '12px' }}>
                    {approvalChoice === 'local'
                      ? 'Local changes will be pushed to the server.'
                      : 'Local cache will be overwritten with server data.'}
                  </IonText>
                )}

                <IonButton
                  expand="block"
                  disabled={!approvalChoice || isCommitting}
                  onClick={() => setShowConfirmAlert(true)}
                >
                  {isCommitting ? (
                    <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                  ) : null}
                  Commit
                </IonButton>
              </div>
            )}
          </div>
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={showConfirmAlert}
        onDidDismiss={() => setShowConfirmAlert(false)}
        header="Confirm Commit"
        message={
          approvalChoice === 'local'
            ? 'Push local changes to the server? This will overwrite server data for this inspection.'
            : 'Override local cache with server data? Local changes will be lost.'
        }
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          { text: 'Commit', handler: () => handleCommit() },
        ]}
      />

      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
    </>
  );
};

export default SyncComparisonModal;
